import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { OfferingSwitchConfirmation } from 'legacy/lut/components/widgets/public/partner360/programTypeButtons/components/programTypeButtonsControl/components/offeringSwitchConfirmation/OfferingSwitchConfirmation'
import { RequestStatus } from 'legacy/lut/configs/service'
import { changeProgramType } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { OfferingIcon } from 'partner360/components/offeringWithIcon'
import { ITabStripItem, TabStrip } from 'partner360/components/tabStrip'
import { Tag } from 'partner360/components/tag'
import { TrainingCentre } from 'partner360/components/trainingCentre/TrainingCentre'
import { PLANNING_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/planningCockpitTourTargets'
import { PROGRAM_TYPE_TAB_ICON_SIZES } from 'partner360/features/financialCockpit/configs/programTypeTabIconSizes'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from 'partner360/features/financialCockpit/configs/translations'
import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'

import { getOfferingTitleKey } from '../../lib/getOfferingTitleKey'
import {
  selectCockpitCategoriesByOffering,
  selectCockpitCategory,
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitOffering,
  selectCockpitSwitchingOnOffering,
  selectFinancialCockpitState,
  setCockpitCategory,
  setCockpitOffering,
} from '../../model'
import {
  isCockpitPlanFinished,
  useCockpitOfferingSwitch,
  useCockpitPlanningStatuses,
} from '../../model/hooks'
import type { ICockpitPlanningStatus } from '../../model/hooks'
import { PlanningStatusLine } from '../cockpitScopePanel/components/cockpitPlanningStatus'
import { CategoryDropdown } from '../components/categoryDropdown'
import { OfferingVisibilityToggle } from '../components/offeringVisibilityToggle'

import styles from './CockpitProgramTypes.module.scss'

interface ICockpitProgramTypesProps {
  partnerId: string
  /** Inside `CockpitScopePanel` the strip has no card of its own and no Training Centre button:
   *  the panel carries both. There a programme taken out of the plan stays open to view, and is
   *  switched back on from the Help & tools panel rather than from its tab. */
  embedded?: boolean
  /** Drawn inside the tab track after the last tab, e.g. the setting of which offerings it holds. */
  trailing?: React.ReactNode
}

export const CockpitProgramTypes = ({
  partnerId,
  embedded = false,
  trailing,
}: ICockpitProgramTypesProps) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()

  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const selectedProgramType = useSelector(selectCockpitOffering)
  const switchingOnOffering = useSelector(selectCockpitSwitchingOnOffering)
  const { fetchingStatus } = useSelector(selectFinancialCockpitState)
  const categoriesByOffering = useSelector(selectCockpitCategoriesByOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const planningStatuses = useCockpitPlanningStatuses(partnerId)

  const {
    isDistributor,
    loadingOffering,
    pendingChange,
    requestChange,
    confirmChange,
    cancelChange,
  } = useCockpitOfferingSwitch(partnerId)

  const availableProgramTypes = useMemo(
    () => [...getAvailableProgramTypes(offeringsCatalog)].sort((a, b) => a.orderBy - b.orderBy),
    [offeringsCatalog]
  )

  const { currentOfferings, enabledOfferings } = useMemo(
    () => getOfferingsInfo(availableProgramTypes),
    [availableProgramTypes]
  )
  const showOverall = enabledOfferings.length !== 1

  const handleSelect = useCallback(
    (programType: ProgramTypes) => {
      if (programType === ProgramTypes.Overall) {
        dispatch(changeProgramType(programType))
        dispatch(setCockpitOffering(programType))
        return
      }

      const isEnabled = enabledOfferings.some((it) => it.programType === programType)
      const isViewable = embedded && currentOfferings.some((it) => it.programType === programType)

      const offering = currentOfferings.find((it) => it.programType === programType)

      if (isEnabled || isViewable) {
        dispatch(changeProgramType(programType))
        dispatch(setCockpitOffering(programType))

        /* A programme taken out of the plan opens to view and asks at once whether to switch it
           back on: without that its figures are not calculated. */
        if (!isEnabled && offering && programType !== selectedProgramType) {
          requestChange({ offering, flag: true })
        }

        return
      }

      if (offering) {
        requestChange({ offering, flag: true })
      }
    },
    [currentOfferings, dispatch, embedded, enabledOfferings, requestChange, selectedProgramType]
  )

  /* Only the embedded strip previews the status: it is the one with the status line under it. */
  const getStatusTooltip = useCallback(
    (status?: ICockpitPlanningStatus) =>
      embedded && status
        ? () => <PlanningStatusLine status={status} layout="column" inverted />
        : undefined,
    [embedded]
  )

  const handleEyeClick = useCallback(
    (event: React.MouseEvent, offering: (typeof currentOfferings)[number]) => {
      event.stopPropagation()
      requestChange({ offering, flag: !offering.enabled })
    },
    [requestChange]
  )

  useEffect(() => {
    /* An empty catalog is only an answer once the data has landed: a partner switched to that has
       no programmes falls back to `Overall` instead of keeping the last partner's programme. */
    if (!offeringsCatalog.length) {
      if (
        fetchingStatus === RequestStatus.FULFILLED &&
        selectedProgramType !== ProgramTypes.Overall
      ) {
        dispatch(changeProgramType(ProgramTypes.Overall))
        dispatch(setCockpitOffering(ProgramTypes.Overall))
      }

      return
    }

    const selectableOfferings = embedded ? currentOfferings : enabledOfferings
    const isValidProgramType =
      (showOverall && selectedProgramType === ProgramTypes.Overall) ||
      selectableOfferings.some((it) => it.programType === selectedProgramType)

    if (!isValidProgramType) {
      const nextProgramType = showOverall ? ProgramTypes.Overall : enabledOfferings[0].programType
      dispatch(changeProgramType(nextProgramType))
      dispatch(setCockpitOffering(nextProgramType))
    }
  }, [
    currentOfferings,
    dispatch,
    embedded,
    enabledOfferings,
    fetchingStatus,
    offeringsCatalog.length,
    selectedProgramType,
    showOverall,
    t,
  ])

  useEffect(() => {
    if (
      selectedCategory &&
      !categoriesByOffering[selectedProgramType]?.some(
        ({ category }) => category === selectedCategory
      )
    ) {
      dispatch(setCockpitCategory(null))
    }
  }, [categoriesByOffering, dispatch, selectedCategory, selectedProgramType])

  const items = useMemo<ITabStripItem<ProgramTypes>[]>(() => {
    const offeringItems = currentOfferings
      .filter((offering) => offering.programType !== ProgramTypes.Overall)
      .map((offering) => {
        const isLoading = loadingOffering === offering.programType
        const isSelected = selectedProgramType === offering.programType
        const isCategorySelected = isSelected && Boolean(selectedCategory)
        const categories = categoriesByOffering[offering.programType] ?? []
        /* The tab carries its icon and title only; the numbers behind the fold are stated by
           What needs attention, and previewed in the tab's tooltip. */
        const status = offering.enabled
          ? planningStatuses.byOffering.get(offering.programType)
          : undefined

        return {
          key: offering.programType,
          icon: (
            <OfferingIcon offering={offering.programType} sizes={PROGRAM_TYPE_TAB_ICON_SIZES} />
          ),
          title: getOfferingTitleKey(offering.programType),
          /* Embedded in `CockpitScopePanel` the category is a chip of its own beside the row, so
             the tab neither repeats it nor carries its caret. */
          secondaryContent:
            isCategorySelected && !embedded ? (
              <Tag variant="info" translation={selectedCategory} />
            ) : undefined,
          muted: !offering.enabled,
          mutedLabel: t(FINANCIAL_COCKPIT_TRANSLATION_KEYS.programTypes.switchedOff),
          loading: switchingOnOffering === offering.programType,
          disabled: isLoading,
          tone: status?.tone,
          count: status?.attentionCount,
          unfinished: status ? !isCockpitPlanFinished(status) : undefined,
          tooltip: getStatusTooltip(status),
          adornment:
            !embedded && isSelected && categories.length > 0 ? (
              <CategoryDropdown
                categories={categories}
                selectedCategory={selectedCategory}
                onChange={(category) => dispatch(setCockpitCategory(category))}
              />
            ) : undefined,
          /* Embedded, the switch lives on the band under the tab (`CockpitOfferingToggle`). */
          cornerAction: embedded ? undefined : (
            <OfferingVisibilityToggle
              enabled={offering.enabled}
              disabled={isLoading}
              onClick={(event: React.MouseEvent) => handleEyeClick(event, offering)}
            />
          ),
        }
      })

    return [
      ...(showOverall
        ? [
            {
              key: ProgramTypes.Overall,
              icon: <i className={cn(styles.overallIcon, 'kbase kbase-buffer')} />,
              title: FINANCIAL_COCKPIT_TRANSLATION_KEYS.programTypes.titles.overall,
              tone: planningStatuses.overall.tone,
              count: planningStatuses.overall.attentionCount,
              unfinished: !isCockpitPlanFinished(planningStatuses.overall),
              tooltip: getStatusTooltip(planningStatuses.overall),
            },
          ]
        : []),
      ...offeringItems,
    ]
  }, [
    categoriesByOffering,
    embedded,
    currentOfferings,
    dispatch,
    getStatusTooltip,
    handleEyeClick,
    loadingOffering,
    planningStatuses,
    selectedCategory,
    selectedProgramType,
    showOverall,
    switchingOnOffering,
    t,
  ])

  return (
    <div
      className={cn(styles.root, { [styles.embedded]: embedded })}
      data-tour-id={TOUR_TARGETS.programmeTabs}
    >
      <TabStrip
        items={items}
        selectedKey={selectedProgramType}
        onSelect={handleSelect}
        appearance={embedded ? 'segmented' : 'underline'}
        trailing={trailing}
      />

      {!embedded && <TrainingCentre />}

      <OfferingSwitchConfirmation
        isShow={pendingChange !== null}
        isDistributor={isDistributor}
        state={pendingChange}
        onConfirm={confirmChange}
        onCancel={cancelChange}
      />
    </div>
  )
}
